import React, { useEffect } from "react";
import { Shield, Heart, Users, UserCheck, Home, Phone, Mail, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-walkthrough/artifacts/x82qaf6p_logo_converted_1772814484394.jpeg";

// Hide Emergent badge on mount for print pages
const useHideEmergentBadge = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        * [class*="emergent"], 
        * [class*="Emergent"],
        * [class*="visual-edits"],
        * [class*="VisualEdits"],
        div[style*="position: fixed"],
        body > div:last-child {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Also try to hide on screen for print preview
    const hideForPrint = () => {
      const badges = document.querySelectorAll('[class*="emergent"], [class*="Emergent"], [class*="visual"]');
      badges.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    
    window.addEventListener('beforeprint', hideForPrint);
    return () => window.removeEventListener('beforeprint', hideForPrint);
  }, []);
};

// Tri-Fold Brochure Component
export const Brochure = () => {
  useHideEmergentBadge();
  
  return (
    <div className="bg-white print:p-0" style={{ width: '11in', height: '8.5in', margin: '0 auto' }}>
      {/* Print Instructions */}
      <div className="print:hidden p-4 bg-[#F4EFEA] border-b border-[#E6DFD3]">
        <h2 className="text-lg font-bold text-[#2E8B57] mb-2">Print Instructions - Tri-Fold Brochure</h2>
        <ol className="list-decimal list-inside text-[#5C5346] text-sm space-y-1">
          <li>Press <strong>Ctrl+P</strong> (or Cmd+P on Mac)</li>
          <li>Set paper size to <strong>Letter (8.5" x 11")</strong></li>
          <li>Set orientation to <strong>Landscape</strong></li>
          <li>Set margins to <strong>None</strong></li>
          <li>Enable <strong>"Background graphics"</strong></li>
          <li>Print or Save as PDF</li>
        </ol>
      </div>

      {/* Brochure Content - Outside */}
      <div className="grid grid-cols-3 print:grid-cols-3" style={{ height: '8.5in' }}>
        
        {/* Panel 1: Back Panel */}
        <div className="bg-[#1A3A2F] text-white p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-[#DAA520] font-bold text-base mb-3">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#DAA520] mt-0.5" />
                <p className="text-sm">(478) 294-8226</p>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-[#DAA520] mt-0.5" />
                <p className="text-xs">lalita@risewithmomentum.com</p>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#DAA520] mt-0.5" />
                <p className="text-sm">Serving Georgia</p>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="w-28 h-28 mx-auto mb-2">
              <img src={LOGO_URL} alt="Logo" className="w-full h-full object-contain" />
            </div>
            <p className="text-[#DAA520] font-bold text-sm">Momentum Rise</p>
            <p className="text-[#DAA520] font-bold text-xs">Enterprise</p>
            <p className="text-white/60 text-xs mt-1">© 2026</p>
          </div>
        </div>

        {/* Panel 2: Front Cover */}
        <div className="bg-[#2E8B57] text-white p-5 flex flex-col justify-center items-center text-center">
          <div className="w-40 h-40 mb-4">
            <img src={LOGO_URL} alt="Momentum Rise Enterprise" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Momentum Rise
          </h1>
          <h2 className="text-lg font-bold text-[#DAA520] mb-4">Enterprise</h2>
          <div className="w-12 h-0.5 bg-[#DAA520] mb-4"></div>
          <p className="text-base italic mb-4">"A place to rise, rebuild, and belong."</p>
          <p className="text-xs opacity-90">Shared, Safe & Structured Living</p>
          <p className="text-xs opacity-90">for Those Who Need It Most</p>
        </div>

        {/* Panel 3: Inside Flap */}
        <div className="bg-[#FDFBF7] p-5 flex flex-col justify-center">
          <h3 className="text-[#2E8B57] font-bold text-lg mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Our Mission
          </h3>
          <p className="text-[#5C5346] text-xs leading-relaxed mb-4">
            Momentum Rise Enterprise was created with the belief that everyone deserves a chance to rise again. We provide dignified, supportive housing for those who need it most.
          </p>
          <p className="text-[#5C5346] text-xs leading-relaxed mb-4">
            Sometimes life brings hardship, change, or uncertainty, but that doesn't mean a person's story is over.
          </p>
          <div className="border-l-4 border-[#DAA520] pl-3">
            <p className="text-[#2E8B57] font-semibold italic text-xs">
              "I believe in second chances, community, and the power of a stable home."
            </p>
            <p className="text-[#5C5346] text-xs mt-1">— Lalita Henry, Founder</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Page Flyer Component
export const Flyer = () => {
  useHideEmergentBadge();
  
  return (
    <div className="bg-white print:p-0" style={{ width: '8.5in', height: '11in', margin: '0 auto' }}>
      {/* Print Instructions */}
      <div className="print:hidden p-4 bg-[#F4EFEA] border-b border-[#E6DFD3]">
        <h2 className="text-lg font-bold text-[#2E8B57] mb-2">Print Instructions - Single Page Flyer</h2>
        <ol className="list-decimal list-inside text-[#5C5346] text-sm space-y-1">
          <li>Press <strong>Ctrl+P</strong> (or Cmd+P on Mac)</li>
          <li>Set paper size to <strong>Letter (8.5" x 11")</strong></li>
          <li>Set orientation to <strong>Portrait</strong></li>
          <li>Set margins to <strong>None</strong></li>
          <li>Enable <strong>"Background graphics"</strong></li>
          <li>Print or Save as PDF</li>
        </ol>
      </div>

      {/* Flyer Content - No Footer to avoid badge */}
      <div style={{ height: '11in' }} className="flex flex-col print:h-full">
        {/* Header with Large Logo */}
        <div className="bg-[#2E8B57] text-white p-6 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src={LOGO_URL} alt="Logo" className="w-24 h-24 object-contain" />
            <div className="text-left">
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Momentum Rise</h1>
              <p className="text-xl font-bold text-[#DAA520]">Enterprise</p>
            </div>
          </div>
          <p className="text-xl italic">"A place to rise, rebuild, and belong."</p>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 bg-[#FDFBF7]">
          {/* Tagline */}
          <div className="text-center mb-5">
            <h2 className="text-2xl font-bold text-[#1C1917] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Shared, Safe & Structured Living
            </h2>
            <p className="text-base text-[#5C5346]">For Veterans, Seniors, Recovery, Re-Entry & Homeless Individuals</p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-5 gap-3 mb-5">
            <div className="text-center p-3 bg-white border border-[#E6DFD3] rounded-lg">
              <Shield className="w-6 h-6 text-[#2E8B57] mx-auto mb-1" />
              <p className="font-bold text-xs text-[#1C1917]">Veterans</p>
            </div>
            <div className="text-center p-3 bg-white border border-[#E6DFD3] rounded-lg">
              <Heart className="w-6 h-6 text-[#DAA520] mx-auto mb-1" />
              <p className="font-bold text-xs text-[#1C1917]">Seniors</p>
            </div>
            <div className="text-center p-3 bg-white border border-[#E6DFD3] rounded-lg">
              <Users className="w-6 h-6 text-[#2E8B57] mx-auto mb-1" />
              <p className="font-bold text-xs text-[#1C1917]">Recovery</p>
            </div>
            <div className="text-center p-3 bg-white border border-[#E6DFD3] rounded-lg">
              <UserCheck className="w-6 h-6 text-[#DAA520] mx-auto mb-1" />
              <p className="font-bold text-xs text-[#1C1917]">Re-Entry</p>
            </div>
            <div className="text-center p-3 bg-white border border-[#E6DFD3] rounded-lg">
              <Home className="w-6 h-6 text-[#2E8B57] mx-auto mb-1" />
              <p className="font-bold text-xs text-[#1C1917]">Homeless</p>
            </div>
          </div>

          {/* What We Offer */}
          <div className="bg-white border border-[#E6DFD3] rounded-xl p-5 mb-5">
            <h3 className="text-lg font-bold text-[#2E8B57] mb-3 text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              What We Offer
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Safe, structured environments</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Shared community living</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Substance-free homes</span>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Structured daily routines</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Pathways to independence</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DAA520] font-bold">✓</span>
                  <span className="text-[#5C5346] text-sm">Supportive community</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="bg-[#2E8B57] text-white p-5 rounded-xl text-center">
              <h4 className="font-bold text-base mb-1">Need Housing?</h4>
              <p className="text-xs opacity-90 mb-2">Take the first step toward stability</p>
              <p className="text-xl font-bold">(478) 294-8226</p>
            </div>
            <div className="bg-[#DAA520] text-[#1C1917] p-5 rounded-xl text-center">
              <h4 className="font-bold text-base mb-1">Want to Partner?</h4>
              <p className="text-xs mb-2">Join our mission to serve Georgia</p>
              <p className="text-sm font-bold">lalita@risewithmomentum.com</p>
            </div>
          </div>

          {/* Contact Bar - replaces footer */}
          <div className="bg-[#1A3A2F] text-white p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Logo" className="w-12 h-12 object-contain" />
                <div>
                  <p className="font-bold text-sm">Momentum Rise Enterprise</p>
                  <p className="text-[#DAA520] text-xs">Lalita Henry, Founder</p>
                </div>
              </div>
              <div className="text-right text-xs">
                <p className="flex items-center justify-end gap-2"><Phone className="w-3 h-3" /> (478) 294-8226</p>
                <p className="flex items-center justify-end gap-2"><Mail className="w-3 h-3" /> lalita@risewithmomentum.com</p>
                <p className="flex items-center justify-end gap-2"><MapPin className="w-3 h-3" /> Serving Georgia</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Brochure;
